import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import PermissionButton from 'components/buttons/PermissionButton';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import React, { useCallback, useMemo } from 'react';
import { useForm, useWatch, ValidateResult } from 'react-hook-form';
import { useAddManuallySelectedCoiCheckRelationMutation } from 'store/api/coi-check';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { CoiCheckAddRelationForm, CoiCheckRelation, CoiCheckRelationDto, CoiCheckScopeType } from 'types/coi-check';
import { NamedEntityMinDto } from 'types/other';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators, renderMatterOption } from 'utils/matterOptionRenderer';
import { useHasPermissions } from 'utils/permissionUtil';
import { ModalFooterSimple } from '../ModalFooterSimple';
import { ModalHeader } from '../ModalHeader';
import { useModal } from '../useModal';

const FORM_OVERFLOW_CUSTOM_STYLE = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
};

interface Props {
    titleId: string;
    initialData: CoiCheckAddRelationForm;
    coiCheckId: string;
    connectedClients: CoiCheckRelation[];
    connectedMatters: CoiCheckRelation[];
    onClose: () => void;
}

// TODO: add UPDATE_COI_CHECKS_ALL and UPDATE_COI_CHECKS_MY_OWN system privileges
const updateCoiCheckPermissions = ['READ.COI_CHECKS.ALL', 'READ.COI_CHECKS.MY_OWN'];

const AddCoiCheckRelationModal = (props: Props) => {
    const [addCoiCheckRelation] = useAddManuallySelectedCoiCheckRelationMutation();

    const { titleId, initialData, onClose, coiCheckId, connectedClients, connectedMatters } = props;

    const { handleSubmit, control, setValue } = useForm<CoiCheckAddRelationForm>({
        defaultValues: initialData,
        values: initialData,
    });

    const [clients, cases] = useWatch({ control, name: ['clients', 'cases'] });

    const mattersFilter = useMemo(() => {
        const filterByClientId = clients.length
            ? `clientParty.id=in=(${clients.map((client) => client.id)})`
            : undefined;
        return filterByClientId;
    }, [clients]);

    const handleOnAddRelationsSubmit = () => {
        handleSubmit((data) => {
            const relations = getCoiCheckRelationsDto(data);
            if (relations.length) {
                addCoiCheckRelation({ entityId: coiCheckId, relations: relations });
            }
            onClose();
        })();
    };

    const getCoiCheckRelationsDto = (form: CoiCheckAddRelationForm): CoiCheckRelationDto[] => {
        const clients: CoiCheckRelationDto[] = form.clients.map((item) => {
            return { coiCheckScopeTypeCode: CoiCheckScopeType.CLIENT, entityName: item.name, entityId: item.id };
        });
        const cases: CoiCheckRelationDto[] = form.cases.map((item) => {
            return { coiCheckScopeTypeCode: CoiCheckScopeType.MATTER, entityName: item.name, entityId: item.id };
        });

        return [...clients, ...cases];
    };

    const isValidClient = useCallback(
        (value: string | number | NamedEntityMinDto | NamedEntityMinDto[] | undefined): ValidateResult => {
            if (!value || cases.length || !connectedClients.length) {
                return true;
            }

            const connectedClientsIdList = connectedClients.map((relation) => relation.entityId);

            if (Array.isArray(value) && value.some((item) => connectedClientsIdList.includes(item.id))) {
                return t('coi.add-relations.client-already-connected');
            }

            return true;
        },
        [cases, connectedClients]
    );

    const isValidMatter = useCallback(
        (value: string | number | NamedEntityMinDto | NamedEntityMinDto[] | undefined): ValidateResult => {
            if (!value || !connectedMatters.length) {
                return true;
            }

            const connectedMattersIdList = connectedMatters.map((relation) => relation.entityId);

            if (Array.isArray(value) && value.some((item) => connectedMattersIdList.includes(item.id))) {
                return t('coi.add-relations.matter-already-connected');
            }

            return true;
        },
        [connectedMatters]
    );

    return (
        <>
            <ModalHeader title={t(titleId)} onCancel={onClose} />
            <div className="h-full overflow-hidden p-6 max-sm:px-4">
                <div className="flex flex-row">
                    <div className="min-w-0 flex-1">
                        <div className="my-4 block">
                            <ContactDynamicMultiSelectPicker
                                setValue={setValue}
                                control={control}
                                label="coi.add-relations.clients"
                                placeholder="coi.add-relations.clients"
                                name="clients"
                                paginated={{
                                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                                    searchField: ['name', 'partyNumber'],
                                }}
                                validate={(val) => isValidClient(val)}
                                PickerModalComponent={ContactPickerModal}
                            />
                        </div>
                        <div className="my-4 block">
                            <DynamicMultiSelectInput
                                setValue={setValue}
                                control={control}
                                label="coi.add-relations.cases"
                                placeholder="coi.add-relations.cases"
                                name="cases"
                                paginated={{
                                    fetch: useLazyGetMattersListQuery,
                                    searchField: ['fullName', 'clientParty.name'],
                                    filters: mattersFilter,
                                    sort: ['clientParty.name,asc', 'name,asc'],
                                }}
                                groupByField="clientParty.name"
                                getOptionLabel="name"
                                getOptionIdentifier="id"
                                getSelectedValueStartAdornment={renderMatterIndicators}
                                customOptionRenderer={renderMatterOption}
                                validate={(val) => isValidMatter(val)}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ModalFooterSimple
                buttons={[
                    <Button {...buttonStyle({ variant: 'outlined', color: 'primary' })} key="cancel" onClick={onClose}>
                        {t('cancel')}
                    </Button>,
                    <PermissionButton
                        permissions={updateCoiCheckPermissions}
                        key="coi-add-relations"
                        color="secondary"
                        variant="contained"
                        onClick={handleOnAddRelationsSubmit}
                    >
                        {t('save')}
                    </PermissionButton>,
                ]}
            />
        </>
    );
};

interface AddRelationButtonProps {
    children?: React.ReactNode;
    coiCheckId: string;
    connectedClients: CoiCheckRelation[];
    connectedMatters: CoiCheckRelation[];
}

const CoiCheckAddRelationButton: React.FC<AddRelationButtonProps> = (props: AddRelationButtonProps) => {
    const { children, coiCheckId, connectedClients, connectedMatters } = props;
    const { openModal, closeModal } = useModal();

    const hasPermissionToUpdateCoiCheck = useHasPermissions(updateCoiCheckPermissions);

    const openAddCoiCheckRelationModal = useCallback(() => {
        openModal(
            <AddCoiCheckRelationModal
                onClose={closeModal}
                titleId={'coi.add-relations.title'}
                coiCheckId={coiCheckId}
                connectedClients={connectedClients}
                connectedMatters={connectedMatters}
                initialData={{
                    clients: [],
                    cases: [],
                }}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    }, [closeModal, openModal, coiCheckId, connectedClients, connectedMatters]);

    return hasPermissionToUpdateCoiCheck ? (
        <button type="button" className="text-link cursor-pointer" onClick={openAddCoiCheckRelationModal}>
            {children}
        </button>
    ) : null;
};

export default CoiCheckAddRelationButton;
